/** @module */

import lozad from 'lozad';

let lazyObs; // lozad instance

/**
 * Function to update the lazyObserver after a DOM change / insertion
 *
 * @export
 */
export function updateObserver() {
  if (lazyObs && lazyObs.observe) {
    lazyObs.observe();
  } else {
    console.warn('Lazyload : lazyObserver was not correctly initialized !');
  }
  if (window.cube.config.anly_useLegacy === 'Y') {
    //reload the analytics observer at the same time
    CUBE.Analytics.enhanced.list.es6.updateObserver();
  }
}

/**
 * Function to trigger the loading of a given DOM node (<img>, <picture>, <iframe>, tag with data-background-image attribute)
 *
 * @export
 * @param {HTMLElement} elt
 */
export function display(elt) {
  if (lazyObs && lazyObs.triggerLoad) {
    lazyObs.triggerLoad(elt);
  }
}

/**
 * Global Lazyload initialization
 *
 * @export
 * @param {String} containerSelector
 */
export function init(containerSelector) {
  const lazySelectorsArr = [
    '.js-img-lazy', //used for <img> tags
    '.js-picture-lazy', // for <picture>
    '.js-iframe-lazy', // for <iframe>
    '.js-background-lazy', // to lazyload a CSS background
    '.js-content-lazy', // to lazyload cartridge
  ];

  let lazySelector;

  if (containerSelector) {
    lazySelector = lazySelectorsArr
      .map(e => `${containerSelector} ${e}`)
      .join(', ');
  } else {
    lazySelector = lazySelectorsArr.join(', ');
  }

  lazyObs = lozad(lazySelector, {
    rootMargin: '10px 0px', // syntax similar to that of CSS Margin
    threshold: 0.1,
    load: loadFun,
    loaded: loadedFun,
  });

  //finallly, launch the observer
  lazyObs.observe();
}

/**
 * Callback called after a lazyload
 *
 * @param {HTMLElement} el
 */
function loadedFun(el) {
  el.classList.add('js-img-lazy-loaded');
}

/**
 * Loading function
 *
 * @param {HTMLElement} el
 */
function loadFun(el) {
  const isIE = document.documentMode;

  if (el.nodeName.toLowerCase() === 'picture') {
    const imgTag = document.createElement('img');

    //IE management
    if (isIE && el.getAttribute('data-iesrc')) {
      imgTag.src = el.getAttribute('data-iesrc');
    }

    // apply alt attribute to the image if corresponding data attribute
    // is found on the <picture> tag
    if (el.getAttribute('data-lazy-img-alt')) {
      imgTag.alt = el.getAttribute('data-lazy-img-alt');
    }

    // apply css class to the image if corresponding data attribute
    // is found on the <picture> tag
    if (el.getAttribute('data-lazy-img-class')) {
      imgTag.className = el.getAttribute('data-lazy-img-class'); // we use classname because the attribute could contain several classnames
    }

    // TNR management
    if (el.getAttribute('data-tnr')) {
      imgTag.dataset = el.getAttribute('data-tnr');
    }

    el.appendChild(imgTag);
  }

  // <img> and <iframe> case
  if (el.getAttribute('data-src')) {
    el.src = el.getAttribute('data-src');
  }

  // <img>
  if (el.getAttribute('data-srcset')) {
    el.srcset = el.getAttribute('data-srcset');
  }

  // CSS background case
  if (el.getAttribute('data-background-image')) {
    el.style.backgroundImage = `url(${el.getAttribute(
      'data-background-image'
    )})`;
  }

  // Async cartridge
  if (el.getAttribute('data-async-content')) {
    asyncCartridgeLoad(el);
  }
}

/**
 * Loads 'async-container' element
 * Mostly used on ProductPage for async-floors
 *
 * @param {DOM Element} el cartridge to load -- must contain data-content & data-params attributes !
 * @param {Function} callback executed after async-catridge is loaded
 */
export const asyncCartridgeLoad = (el, callback) => {
  el.classList.add('dkt-loading');

  const url = `asyncCartridgeLoad?contentPath=${el.getAttribute(
    'data-async-content'
  )}&params=${encodeURIComponent(el.getAttribute('data-params'))}`;

  cube.ajax
    .get(url)
    .onSuccess(resp => {
      if (resp.length > 0 && typeof resp !== 'object') {
        cube.$id(el.id).innerHTML = resp;
        cube.event.send(`async-content:loaded`, el);
        cube.event.send(`async-content:${el.id}`, el);
        updateObserver();
      }
      el.classList.remove('dkt-loading', 'async-container');
      if (callback && typeof callback === 'function') callback();
    })
    .call();
};



// WEBPACK FOOTER //
// ./src/modules-es5/commons/lib/lib-lazyload.js